import type { EditorHost } from '@blocksuite/block-std';
import { BlockService } from '@blocksuite/block-std';
import { assertExists } from '@blocksuite/global/utils';
import { Slot } from '@blocksuite/store';
import { render } from 'lit';

import type { Html } from '../_common/adapters/html.js';
import { UriListAdapter } from '../_common/adapters/uri-list.js';
import {
  FileDropManager,
  type FileDropOptions,
} from '../_common/components/file-drop-manager.js';
import { EMBED_CARD_HEIGHT, EMBED_CARD_WIDTH } from '../_common/consts.js';
import { matchFlavours } from '../_common/utils/model.js';
import { isInsideEdgelessEditor } from '../_common/utils/query.js';
import { addSiblingImageBlock } from '../image-block/utils.js';
import type { EdgelessRootBlockComponent } from '../root-block/edgeless/edgeless-root-block.js';
import type { RootBlockComponent } from '../root-block/types.js';
import type { DragHandleOption } from '../root-block/widgets/drag-handle/config.js';
import {
  AFFINE_DRAG_HANDLE_WIDGET,
  AffineDragHandleWidget,
} from '../root-block/widgets/drag-handle/drag-handle.js';
import {
  captureEventTarget,
  convertDragPreviewDocToEdgeless,
  convertDragPreviewEdgelessToDoc,
} from '../root-block/widgets/drag-handle/utils.js';
import type { AttachmentBlockComponent } from './attachment-block.js';
import {
  type AttachmentBlockModel,
  AttachmentBlockSchema,
} from './attachment-model.js';
import {
  addSiblingAttachmentBlocks,
  fetchImageFile,
  fetchMediaFile,
  isImage,
  isMedia,
} from './utils.js';

export class AttachmentService extends BlockService<AttachmentBlockModel> {
  get rootElement(): RootBlockComponent {
    const rootModel = this.doc.root;
    assertExists(rootModel);

    const rootElement = this.std.view.viewFromPath('block', [
      rootModel.id,
    ]) as RootBlockComponent | null;
    assertExists(rootElement);
    return rootElement;
  }

  maxFileSize = 10 * 1000 * 1000; // 10MB (default)

  slots = {
    onFilesDropped: new Slot<File[]>(),
  };

  private _fileDropOptions: FileDropOptions = {
    flavour: this.flavour,
    onDrop: async ({ files, html, uriList, targetModel, place, point }) => {
      if (!files.length && !html && !uriList?.length) return false;

      // in `page` mode or in edgeless's note
      const isInline =
        targetModel && !matchFlavours(targetModel, ['affine:surface']);
      const isInEdgeless = isInsideEdgelessEditor(this.host as EditorHost);
      const edgeless = isInEdgeless
        ? (this.rootElement as EdgelessRootBlockComponent)
        : null;

      // generic attachment block for all files except images
      const attachmentFiles = files.filter(
        file => !file.type.startsWith('image/')
      );

      const elements = [];

      if (attachmentFiles.length) {
        if (isInline) {
          await addSiblingAttachmentBlocks(
            this.host as EditorHost,
            attachmentFiles,
            this.maxFileSize,
            targetModel,
            place
          );
        } else if (isInEdgeless && edgeless) {
          const p = edgeless.service.viewport.toViewPointFromClientPoint(point);
          const ids = await edgeless.addAttachments(attachmentFiles, p);
          elements.push(...ids);
        }

        this.slots.onFilesDropped.emit(attachmentFiles);
      }

      if (uriList?.length) {
        const adapter = new UriListAdapter();
        const urls = adapter.parse(uriList);
        const proxy = this.std.clipboard.configs.get('imageProxy');

        if (isInline) {
          const parent = this.doc.getParent(targetModel);
          assertExists(parent);

          const items = [];

          for (const url of urls) {
            if (isImage(url)) {
              items.push(fetchImageFile(url, proxy));
            } else if (isMedia(url)) {
              items.push(fetchMediaFile(url, proxy));
            } else {
              items.push(
                Promise.resolve({
                  url,
                  type: 'embed',
                })
              );
            }
          }

          const list = (await Promise.all(items)).filter(item => item) as {
            url: string;
            type: string;
            file?: File;
          }[];

          let prevModel = targetModel;
          for (const { url, type, file } of list) {
            if (type === 'image' && file) {
              const result = addSiblingImageBlock(
                this.host as EditorHost,
                [file],
                this.maxFileSize,
                prevModel,
                place
              );
              if (result) {
                prevModel = this.std.doc.getBlockById(result[0])!;
              }
            } else if (type === 'attachment' && file) {
              const result = await addSiblingAttachmentBlocks(
                this.host as EditorHost,
                [file],
                this.maxFileSize,
                prevModel,
                place
              );
              if (result) {
                prevModel = this.std.doc.getBlockById(result[0])!;
              }
            } else {
              const [id] = this.std.doc.addSiblingBlocks(
                prevModel,
                [
                  {
                    flavour: 'affine:bookmark',
                    url,
                  },
                ],
                place
              );
              prevModel = this.std.doc.getBlockById(id)!;
            }
          }
        } else if (isInEdgeless && edgeless) {
          const p = edgeless.service.viewport.toViewPointFromClientPoint(point);
          const images = [];
          const attachments = [];
          const embeds = [];

          for (const url of urls) {
            if (isImage(url)) {
              images.push(url);
            } else if (isMedia(url)) {
              attachments.push(url);
            } else {
              embeds.push(url);
            }
          }

          const imageFiles = (
            await Promise.all(images.map(url => fetchImageFile(url, proxy)))
          ).filter(f => f) as File[];

          const attachmentFiles = (
            await Promise.all(
              attachments.map(url => fetchMediaFile(url, proxy))
            )
          ).filter(f => f) as File[];

          if (imageFiles.length) {
            const ids = await edgeless.addImages(imageFiles, p);
            elements.push(...ids);
          }
          if (attachmentFiles.length) {
            const ids = await edgeless.addAttachments(attachmentFiles, p);
            elements.push(...ids);
          }

          if (embeds.length) {
            const [x, y] = edgeless.service.viewport.toModelCoord(p.x, p.y);
            const ids = embeds.map(url =>
              edgeless.clipboardController.createEmbedBlockWith(url, {
                x,
                y,
              })
            );
            elements.push(...ids);
          }
        }
      } else if (html) {
        try {
          const snapshot = await this.std.clipboard
            .getAdapter<Html>('text/html')
            ?.toBlockSnapshot({
              file: html,
              assets: this.std.clipboard.assetsManager,
            });

          if (snapshot) {
            let parentId;
            let index = 0;
            let n = 0;

            if (isInline) {
              const parent = this.doc.getParent(targetModel);
              assertExists(parent);
              parentId = parent.id;

              const targetIndex =
                parent.children.findIndex(({ id }) => id === targetModel.id) ??
                0;
              index = place === 'before' ? targetIndex : targetIndex + 1;
            } else if (isInEdgeless && edgeless) {
              const p =
                edgeless.service.viewport.toViewPointFromClientPoint(point);
              parentId = edgeless.addNoteWithPoint(p);
              elements.push(parentId);
            }

            if (parentId) {
              for (const block of snapshot.children) {
                await this.std.clipboard.pasteBlockSnapshot(
                  block,
                  this.std.doc,
                  parentId,
                  index + n++
                );
              }
            }
          }
        } catch (err) {
          console.error(err);
        }
      }

      if (!isInline && isInEdgeless && edgeless && elements.length) {
        edgeless.service.selection.set({
          elements,
          editing: false,
        });
      }

      return true;
    },
  };

  fileDropManager!: FileDropManager;

  private _dragHandleOption: DragHandleOption = {
    flavour: AttachmentBlockSchema.model.flavour,
    edgeless: true,
    onDragStart: ({ state, startDragging, anchorBlockPath, editorHost }) => {
      if (!anchorBlockPath) return false;
      const anchorComponent = editorHost.std.view.viewFromPath(
        'block',
        anchorBlockPath
      );
      if (
        !anchorComponent ||
        !matchFlavours(anchorComponent.model, [
          AttachmentBlockSchema.model.flavour,
        ])
      )
        return false;

      const blockComponent = anchorComponent as AttachmentBlockComponent;
      const element = captureEventTarget(state.raw.target);

      const isDraggingByDragHandle = !!element?.closest(
        AFFINE_DRAG_HANDLE_WIDGET
      );
      const isDraggingByComponent = blockComponent.contains(element);
      const isInSurface = blockComponent.isInSurface;

      if (!isInSurface && (isDraggingByDragHandle || isDraggingByComponent)) {
        editorHost.selection.setGroup('note', [
          editorHost.selection.create('block', {
            path: blockComponent.path,
          }),
        ]);
        startDragging([blockComponent], state);
        return true;
      } else if (isInSurface && isDraggingByDragHandle) {
        const attachmentPortal = blockComponent.closest(
          '.edgeless-block-portal-attachment'
        );
        assertExists(attachmentPortal);
        const dragPreviewEl = attachmentPortal.cloneNode() as HTMLElement;
        dragPreviewEl.style.transform = '';
        dragPreviewEl.style.left = '0';
        dragPreviewEl.style.top = '0';
        render(
          blockComponent.host.renderModel(blockComponent.model),
          dragPreviewEl
        );

        startDragging([blockComponent], state, dragPreviewEl);
        return true;
      }
      return false;
    },
    onDragEnd: props => {
      const { state, draggingElements, editorHost } = props;
      if (
        draggingElements.length !== 1 ||
        !matchFlavours(draggingElements[0].model, [
          AttachmentBlockSchema.model.flavour,
        ])
      )
        return false;

      const blockComponent = draggingElements[0] as AttachmentBlockComponent;
      const isInSurface = blockComponent.isInSurface;
      const target = captureEventTarget(state.raw.target);
      const isTargetEdgelessContainer =
        target?.classList.contains('edgeless') &&
        target?.classList.contains('affine-block-children-container');

      if (isInSurface) {
        const style = blockComponent.model.style;
        const targetStyle = style === 'cubeThick' ? 'horizontalThin' : style;
        return convertDragPreviewEdgelessToDoc({
          blockComponent,
          style: targetStyle,
          ...props,
        });
      } else if (isTargetEdgelessContainer) {
        let style = blockComponent.model.style ?? 'cubeThick';
        const embed = blockComponent.model.embed;
        if (embed) {
          style = 'cubeThick';
          editorHost.doc.updateBlock(blockComponent.model, {
            style,
            embed: false,
          });
        }

        return convertDragPreviewDocToEdgeless({
          blockComponent,
          cssSelector: '.affine-attachment-container',
          width: EMBED_CARD_WIDTH[style],
          height: EMBED_CARD_HEIGHT[style],
          ...props,
        });
      }

      return false;
    },
  };

  override mounted(): void {
    super.mounted();

    this.fileDropManager = new FileDropManager(this, this._fileDropOptions);

    this.disposables.add(
      AffineDragHandleWidget.registerOption(this._dragHandleOption)
    );
  }
}
